<template>
  <div id="foodlist">
    <!-- Sticky wrapper for topbar + search -->
    <div id="sticky-wrapper">
      <!-- Topbar -->
      <div id="topbar">
        <div class="tools"></div>
        <div class="foodnametitle"
             :style="{ width: doseratio > 0 ? 'calc(55% - 50px)' : 'calc(70% - 50px)' }">
          Your Foods
          <span v-if="showCarbs"> (carbs/100g)</span>
        </div>
        <div>Food Weight</div>
        <div v-if="doseratio > 0">Insulin Dose</div>
        <div>Carbs</div>
      </div>

      <!-- Search bar -->
      <div id="searchbar">
        <input
          ref="searchInput"
          type="text"
          v-model="searchQuery"
          placeholder="Search foods..."
          @input="scrollToMatch"
        />
        <span v-if="searchQuery" class="clear-search" @click="clearSearch">×</span>
      </div>
    </div>

    <!-- Food rows -->
    <div
      v-for="(foodrow, index) in filteredCarblist"
      :key="foodrow.id"
      class="outerrow"
      :class="{ odd: index % 2 === 1 }"
      :ref="foodrow.id"
    >
      <food-row
        :fooditem="foodrow"
        :doseratio="doseratio"
        :show-carbs="showCarbs"
        @delete-item="confirmDelete(foodrow)"
        @updated="onRowUpdated"
      />
    </div>

    <!-- Sticky Totals Row -->
    <div class="totalrow">
      <div class="total-cell tools">
        <div class="buttons-container">
          <!-- Settings -->
          <span id="settings" @click="showSettings = !showSettings">
            <svg class="settings-icon" width="24" height="24" viewBox="0 0 24 24" fill="white">
              <path d="M12 8a4 4 0 100 8 4 4 0 000-8zm10 4a9.99 9.99 0 01-1.19 4.86l2.12 2.12-1.41 1.41-2.12-2.12A9.99 9.99 0 0112 22a9.99 9.99 0 01-4.86-1.19l-2.12 2.12-1.41-1.41 2.12-2.12A9.99 9.99 0 012 12a9.99 9.99 0 011.19-4.86L1.07 5.02l1.41-1.41 2.12 2.12A9.99 9.99 0 0112 2a9.99 9.99 0 014.86 1.19l2.12-2.12 1.41 1.41-2.12 2.12A9.99 9.99 0 0122 12z"/>
            </svg>
          </span>

          <!-- Add Food -->
          <div class="add-container" @click="showAddFood = true">
            <span class="addfood-btn">+</span>
          </div>

          <!-- Clear -->
          <div class="clear-container" @click="showConfirmClear = true">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="white" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </div>
        </div>
      </div>

      <div class="total-cell foodname"></div>
      <div class="total-cell"></div>
      <div class="total-cell">Totals</div>
      <div class="total-cell" v-if="doseratio > 0">{{ totalInsulin }}</div>
      <div class="total-cell">{{ totalCarbs }}</div>
    </div>

    <!-- Panels -->
    <Settings
      v-if="showSettings"
      :show-carbs="showCarbs"
      :show-clear-foods="showClearFoods"
      :insulin-ratio="doseratio"
      @close="showSettings = false"
      @update-insulin="onInsulinUpdated"
      @toggle-show-carbs="onShowCarbsToggled"
      @toggle-clear-foods="onClearFoodsToggled"
      @imported-data="onImportedData"
    />

    <AddFood v-if="showAddFood" @close="showAddFood = false" @add-food="addFood"/>
    <ConfirmClear v-if="showConfirmClear" @close="showConfirmClear = false" @confirm="clearWeights"/>
    <ConfirmDelete
      v-if="showConfirmDelete"
      :food-name="itemToDelete ? itemToDelete.name : ''"
      @close="showConfirmDelete = false"
      @confirm="removeItem(itemToDelete ? itemToDelete.id : null)"
    />
  </div>
</template>

<script>
import FoodRow from "../components/FoodRow.vue";
import Settings from "./Settings.vue";
import AddFood from "./AddFood.vue";
import ConfirmClear from "./ConfirmClear.vue";
import ConfirmDelete from "./ConfirmDelete.vue";

export default {
  name: "FoodList",
  components: { FoodRow, Settings, AddFood, ConfirmClear, ConfirmDelete },
  data() {
    return {
      doseratio: Number(localStorage.getItem("insulinRatio")) ?? 1,
      demolist: { carblist: [] },
      showSettings: false,
      showAddFood: false,
      showConfirmClear: false,
      showConfirmDelete: false,
      itemToDelete: null,
      showCarbs: localStorage.getItem("showCarbs") === "true",
      showClearFoods: localStorage.getItem("clearFoods") === "true",
      searchQuery: "",
    };
  },
  mounted() {
    if (localStorage.carbdatalist) {
      try { this.demolist = JSON.parse(localStorage.carbdatalist); } 
      catch { this.demolist = { carblist: [] }; }
    }
    if (this.showClearFoods) this.clearWeights();
  },
  methods: {
    addFood(newFood) {
      this.demolist.carblist.push(newFood);
      this.saveToLocalStorage();
    },
    confirmDelete(food) {
      this.itemToDelete = food;
      this.showConfirmDelete = true;
    },
    removeItem(itemId) {
      if (!itemId) return;
      this.demolist.carblist = this.demolist.carblist.filter(f => f.id !== itemId);
      this.saveToLocalStorage();
      this.showConfirmDelete = false;
      this.itemToDelete = null;
    },
    clearWeights() {
      this.demolist.carblist.forEach(f => (f.weight = null));
      this.saveToLocalStorage();
      this.showConfirmClear = false;
    },
    onInsulinUpdated(newRatio) {
      this.doseratio = Number(newRatio) || 0;
      localStorage.setItem("insulinRatio", String(this.doseratio));
    },
    onShowCarbsToggled(newValue) {
      this.showCarbs = newValue;
      localStorage.setItem("showCarbs", newValue);
    },
    onClearFoodsToggled(newValue) {
      this.showClearFoods = newValue;
      localStorage.setItem("clearFoods", newValue);
    },
    onRowUpdated() { this.saveToLocalStorage(); },
    saveToLocalStorage() { localStorage.setItem("carbdatalist", JSON.stringify(this.demolist)); },
    onImportedData(newData) {
      this.demolist = newData;
      this.saveToLocalStorage();
    },
    scrollToMatch() {
      this.$nextTick(() => {
        if (!this.searchQuery) return;
        const firstMatch = this.filteredCarblist[0];
        if (firstMatch) {
          const element = this.$refs[firstMatch.id][0];
          if (element) element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      });
    },
    clearSearch() {
      this.searchQuery = "";
      this.$nextTick(() => { this.$refs.searchInput?.focus(); });
    }
  },
  computed: {
    filteredCarblist() {
      if (!this.searchQuery) return this.sortedCarblist;
      const query = this.searchQuery.toLowerCase();
      return this.sortedCarblist.filter(f => f.name.toLowerCase().includes(query));
    },
    sortedCarblist() {
      return [...this.demolist.carblist].sort((a,b) => a.name.localeCompare(b.name));
    },
    totalCarbs() {
      return this.demolist.carblist.reduce((acc,row) => acc + (row.weight*row.carbs)/100,0)
        .toLocaleString(undefined,{minimumFractionDigits:0, maximumFractionDigits:2});
    },
    totalInsulin() {
      const ratio = Number(this.doseratio) || 1;
      return this.demolist.carblist.reduce((acc,row) => acc + ((row.weight*row.carbs)/100)/ratio,0)
        .toLocaleString(undefined,{minimumFractionDigits:0, maximumFractionDigits:2});
    },
  },
};
</script>

<style scoped>
#foodlist { padding-bottom: 70px; }

#sticky-wrapper {
  position: sticky;
  top: 0;
  z-index: 200;
  background-color: #000;
  box-shadow: 0 2px 6px rgba(0,0,0,0.5); /* subtle shadow under the wrapper */
}


#topbar {
  display: flex;
  background-color: #000;
  color: #fff;
  font-family: calibri;
  text-align: left;
}
#topbar div { display: inline-block; width: 15%; text-transform: uppercase; text-align:center; padding:4px 0; }
#topbar div.tools { width:50px; min-width:50px; }
#topbar div.foodnametitle { text-align:right; padding-right:5px; }
#topbar div.foodnametitle span { font-size:12px; }

#searchbar {
  display: flex;
  align-items: center;
  padding: 0 10px;
  border-bottom: 1px solid #333;
}
#searchbar input {
  flex: 1;
  padding: 6px 0;
  border: none;
  outline: none;
  background: transparent;
  color: #fff;
  font-size: 14px;
  font-family: calibri;
  text-transform: uppercase;
}
#searchbar input::placeholder { color: #aaa; }
#searchbar .clear-search {
  cursor: pointer;
  color: #fff;
  font-size: 16px;
  margin-left: 8px;
  user-select: none;
}

/* totals row */
.totalrow {
  display: flex;
  width: 100%;
  background: #000;
  color: #fff;
  font-family: calibri;
  position: fixed;
  bottom: 0;
  left: 0;
  z-index: 100;
  box-shadow: 0 -2px 5px rgba(0,0,0,0.5);
}
.total-cell { padding:10px; text-align:center; flex:1; white-space:nowrap; }
.total-cell.tools { flex:0 0 120px; display:flex; align-items:center; justify-content:flex-start; }
.total-cell.foodname { flex:2; font-weight:bold; }
.buttons-container { display:flex; gap:8px; align-items:center; }
.add-container,.clear-container { display:flex; align-items:center; justify-content:center; background:#fff; border-radius:6px; cursor:pointer; width:28px; height:28px; }
.add-container:hover,.clear-container:hover { background:#e0e0e0; }
.addfood-btn,.clear-btn { font-size:24px; color:#000; line-height:1; user-select:none; }

/* row stripes */
#foodlist > div.outerrow:nth-child(odd) .row { background-color:#f5f5f5; }
#foodlist > div.outerrow:nth-child(even) .row { background-color:#fff; }
</style>
